import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { toast } from "react-toastify";
import {
  FaUserEdit,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaUserShield,
  FaSave,
  FaChevronDown,
  FaChevronUp,
  FaArrowLeft,
} from "react-icons/fa";
import "./AdminManagement.css";

// Permission groups for organized display
const PERMISSION_GROUPS = {
  artist: {
    label: "Artist Management",
    icon: "🎤",
    permissions: ["artist:view", "artist:create", "artist:edit", "artist:delete"],
  },
  song: {
    label: "Song Management",
    icon: "🎵",
    permissions: ["song:view", "song:create", "song:edit", "song:delete"],
  },
  album: {
    label: "Album Management",
    icon: "💿",
    permissions: ["album:view", "album:create", "album:edit", "album:delete"],
  },
  playlist: {
    label: "Playlist Management",
    icon: "📋",
    permissions: ["playlist:view", "playlist:create", "playlist:edit", "playlist:delete"],
  },
  quiz: {
    label: "Quiz Management",
    icon: "❓",
    permissions: ["quiz:view", "quiz:create", "quiz:edit", "quiz:delete"],
  },
  genre: {
    label: "Genre Management",
    icon: "🏷️",
    permissions: ["genre:view", "genre:create", "genre:edit", "genre:delete"],
  },
  news: {
    label: "News Management",
    icon: "📰",
    permissions: ["news:view", "news:create", "news:edit", "news:delete"],
  },
  cache: {
    label: "Cache Management",
    icon: "⚡",
    permissions: ["cache:view", "cache:manage"],
  },
  contact: {
    label: "Contact Management",
    icon: "📞",
    permissions: ["contact:view", "contact:manage"],
  },
  subscribers: {
    label: "Subscribers Management",
    icon: "📧",
    permissions: ["subscribers:view", "subscribers:manage"],
  },
  youtube: {
    label: "YouTube Management",
    icon: "📺",
    permissions: ["youtube:view", "youtube:manage"],
  },
  podcast: {
    label: "Podcast Management",
    icon: "🎙️",
    permissions: ["podcast:view", "podcast:manage"],
  },
  songLyricsContributions: {
    label: "Song Lyrics Contributions",
    icon: "📝",
    permissions: ["song-lyrics-contributions:view", "song-lyrics-contributions:manage"],
  },
  albumContributions: {
    label: "Album Contributions",
    icon: "💿",
    permissions: ["album-contributions:view", "album-contributions:manage"],
  },
  songContributions: {
    label: "Song Contributions",
    icon: "🎵",
    permissions: ["song-contributions:view", "song-contributions:manage"],
  },
  artistBioContributions: {
    label: "Artist Bio Contributions",
    icon: "👤",
    permissions: ["artist-bio-contributions:view", "artist-bio-contributions:manage"],
  },
  videoContributions: {
    label: "Video Contributions",
    icon: "🎬",
    permissions: ["video-contributions:view", "video-contributions:manage"],
  },
  tags: {
    label: "Tags Management",
    icon: "🏷️",
    permissions: ["tags:view", "tags:manage"],
  },
  userActivity: {
    label: "User Activity",
    icon: "👁️",
    permissions: ["user-activity:view"],
  },
  user: {
    label: "User Management",
    icon: "👤",
    permissions: ["user:view", "user:edit", "user:delete"],
  },
  notification: {
    label: "Notification Management",
    icon: "🔔",
    permissions: ["notification:view", "notification:create", "notification:delete"],
  },
};

export default function EditAdmin() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(["artist", "song"]);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    role: "admin",
    isActive: true,
    permissions: [],
  });

  const [originalAdmin, setOriginalAdmin] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAdmin();
  }, [id]);

  const fetchAdmin = async () => {
    try {
      setLoading(true);
      const response = await allAPiServicesCall.getAdminById(id, getAuthConfig());

      if (response?.success && response.data) {
        const admin = response.data;
        setOriginalAdmin(admin);
        setForm({
          fullName: admin.fullName || "",
          email: admin.email || "",
          mobile: admin.mobile || "",
          password: "",
          confirmPassword: "",
          role: admin.role || "admin",
          isActive: admin.isActive !== false,
          permissions: admin.permissions || [],
        });

        // Expand groups that have selected permissions
        const groupsWithPermissions = Object.entries(PERMISSION_GROUPS)
          .filter(([key, group]) => group.permissions.some((p) => admin.permissions?.includes(p)))
          .map(([key]) => key);
        if (groupsWithPermissions.length > 0) {
          setExpandedGroups(groupsWithPermissions);
        }
      }
    } catch (error) {
      console.error("Error fetching admin:", error);
      toast.error("Failed to load admin details");
      navigate("/admin-management");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const togglePermission = (permission) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const toggleGroupAll = (groupKey) => {
    const groupPermissions = PERMISSION_GROUPS[groupKey].permissions;
    const allSelected = groupPermissions.every((p) => form.permissions.includes(p));

    setForm((prev) => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter((p) => !groupPermissions.includes(p))
        : [...new Set([...prev.permissions, ...groupPermissions])],
    }));
  };

  const toggleGroupExpand = (groupKey) => {
    setExpandedGroups((prev) =>
      prev.includes(groupKey)
        ? prev.filter((g) => g !== groupKey)
        : [...prev, groupKey]
    );
  };

  const validate = () => {
    const newErrors = {};

    if (!form.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!form.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    }

    // Password validation only if password is being changed
    if (form.password) {
      if (form.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters";
      }
      if (form.password !== form.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setSaving(true);

      const payload = {
        fullName: form.fullName.trim(),
        mobile: form.mobile.trim(),
        role: form.role,
        isActive: form.isActive,
        permissions: form.role === "superadmin" ? [] : form.permissions,
      };

      // Only include password if it's being changed
      if (form.password) {
        payload.password = form.password;
      }

      const response = await allAPiServicesCall.updateAdminUser(id, payload, getAuthConfig());

      if (response?.success) {
        navigate("/admin-management");
      }
    } catch (error) {
      console.error("Error updating admin:", error);
    } finally {
      setSaving(false);
    }
  };

  const formatPermissionLabel = (permission) => {
    const [module, action] = permission.split(":");
    return `${action.charAt(0).toUpperCase() + action.slice(1)} ${module.replace("-", " ")}`;
  };

  const formatDate = (date) => {
    if (!date) return "Never";
    const parsedDate = new Date(date);
    // Check if date is valid
    if (isNaN(parsedDate.getTime())) return "Not Available";
    return parsedDate.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Extract creation date from MongoDB ObjectId if createdAt is not available
  const getCreatedAtDate = () => {
    if (originalAdmin?.createdAt) {
      return formatDate(originalAdmin.createdAt);
    }
    // Extract timestamp from MongoDB ObjectId (first 8 hex chars = timestamp)
    if (originalAdmin?._id && typeof originalAdmin._id === 'string' && originalAdmin._id.length === 24) {
      const timestamp = parseInt(originalAdmin._id.substring(0, 8), 16) * 1000;
      return formatDate(new Date(timestamp));
    }
    return "Not Available";
  };

  if (loading) {
    return (
      <CommonLayout>
        <div className="admin-management-page container-fluid px-2 px-md-4">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading admin details...</p>
          </div>
        </div>
      </CommonLayout>
    );
  }

  return (
    <CommonLayout>
      <div className="admin-management-page container-fluid px-2 px-md-4">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-10">
            {/* Header */}
            <div className="mb-4">
              <button
                className="btn btn-link text-muted px-0 mb-2"
                onClick={() => navigate("/admin-management")}
              >
                <FaArrowLeft className="me-2" />
                Back to Admin List
              </button>
              <h2>
                <FaUserEdit className="me-2" />
                Edit Admin
              </h2>
              <p className="text-muted">
                Editing: <strong>{originalAdmin?.fullName}</strong> ({originalAdmin?.email})
              </p>
            </div>

            {/* Admin Info Card - Enhanced Design */}
            <div className="card mb-4 admin-profile-card overflow-hidden border-0 shadow">
              {/* Gradient Header with Avatar and Info */}
              <div
                className="px-4 py-4"
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
              >
                <div className="d-flex flex-column flex-md-row align-items-center gap-4">
                  {/* Avatar */}
                  <div
                    className="avatar shadow"
                    style={{
                      width: 90,
                      height: 90,
                      borderRadius: "50%",
                      background: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "2rem",
                      fontWeight: 700,
                      border: "3px solid rgba(255, 255, 255, 0.5)",
                      flexShrink: 0
                    }}
                  >
                    {originalAdmin?.fullName?.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase()}
                  </div>

                  {/* Name and Email */}
                  <div className="text-center text-md-start flex-grow-1">
                    <h3 className="mb-1 fw-bold text-white">
                      {originalAdmin?.fullName}
                    </h3>
                    <p className="mb-0" style={{ color: "rgba(255, 255, 255, 0.85)" }}>
                      <FaEnvelope className="me-2" size={14} />
                      {originalAdmin?.email}
                    </p>
                  </div>

                  {/* Badges */}
                  <div className="d-flex flex-wrap gap-2 justify-content-center">
                    <span
                      className="badge rounded-pill px-3 py-2"
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        background: originalAdmin?.role === "superadmin"
                          ? "rgba(255, 193, 7, 0.9)"
                          : "rgba(255, 255, 255, 0.2)",
                        color: originalAdmin?.role === "superadmin" ? "#1a1a2e" : "white",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.3)"
                      }}
                    >
                      <FaUserShield className="me-1" />
                      {originalAdmin?.role === "superadmin" ? "Super Admin" : "Admin"}
                    </span>
                    <span
                      className="badge rounded-pill px-3 py-2"
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        background: originalAdmin?.isActive !== false
                          ? "rgba(34, 197, 94, 0.9)"
                          : "rgba(239, 68, 68, 0.9)",
                        color: "white",
                        border: "1px solid rgba(255, 255, 255, 0.3)"
                      }}
                    >
                      <span
                        className="d-inline-block me-1 rounded-circle"
                        style={{
                          width: 8,
                          height: 8,
                          backgroundColor: "white",
                          boxShadow: "0 0 8px rgba(255,255,255,0.8)"
                        }}
                      />
                      {originalAdmin?.isActive !== false ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stats Row */}
              <div className="card-body px-4 py-3 bg-white">
                <div className="d-flex flex-row gap-3">
                  <div
                    className="p-3 rounded-3 flex-fill"
                    style={{
                      background: "linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)",
                      border: "1px solid rgba(102, 126, 234, 0.15)"
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: 42,
                          height: 42,
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          flexShrink: 0
                        }}
                      >
                        <FaUser size={16} />
                      </div>
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          Account Created
                        </small>
                        <span className="fw-semibold" style={{ color: "#1a1a2e", fontSize: "0.9rem" }}>
                          {getCreatedAtDate()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    className="p-3 rounded-3 flex-fill"
                    style={{
                      background: "linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%)",
                      border: "1px solid rgba(34, 197, 94, 0.15)"
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: 42,
                          height: 42,
                          background: "linear-gradient(135deg, #22c55e 0%, #10b981 100%)",
                          color: "white",
                          flexShrink: 0
                        }}
                      >
                        <FaLock size={16} />
                      </div>
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          Last Login
                        </small>
                        <span className="fw-semibold" style={{ color: "#1a1a2e", fontSize: "0.9rem" }}>
                          {formatDate(originalAdmin?.lastLogin)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <form className="admin-form" onSubmit={handleSubmit}>
              <div className="card mb-4">
                <div className="card-header bg-white py-3">
                  <h5 className="mb-0 d-flex align-items-center">
                    <FaUser className="me-2 text-primary" />
                    Basic Information
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <label className="form-label">
                        <FaUser className="me-2 text-muted" />
                        Full Name *
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.fullName ? "is-invalid" : ""}`}
                        name="fullName"
                        value={form.fullName}
                        onChange={handleChange}
                        placeholder="Enter full name"
                      />
                      {errors.fullName && <div className="invalid-feedback">{errors.fullName}</div>}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">
                        <FaEnvelope className="me-2 text-muted" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        value={form.email}
                        disabled
                        style={{ backgroundColor: "var(--bg-secondary)" }}
                      />
                      <small className="text-muted">Email cannot be changed</small>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">
                        <FaPhone className="me-2 text-muted" />
                        Mobile Number *
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
                        name="mobile"
                        value={form.mobile}
                        onChange={handleChange}
                        placeholder="Enter mobile number"
                      />
                      {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">
                        <FaUserShield className="me-2 text-muted" />
                        Role *
                      </label>
                      <select
                        className="form-select"
                        name="role"
                        value={form.role}
                        onChange={handleChange}
                      >
                        <option value="admin">Admin</option>
                        <option value="superadmin">Super Admin</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="isActive"
                          checked={form.isActive}
                          onChange={handleChange}
                          id="isActive"
                          style={{ width: 48, height: 24 }}
                        />
                        <label className="form-check-label ms-2" htmlFor="isActive">
                          Account Active
                          <small className="text-muted d-block">
                            Inactive accounts cannot log in to the admin panel
                          </small>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Password Change Section */}
              <div className="card mb-4">
                <div className="card-header bg-white py-3">
                  <h5 className="mb-0 d-flex align-items-center">
                    <FaLock className="me-2 text-primary" />
                    Change Password
                    <span className="badge bg-secondary ms-2">Optional</span>
                  </h5>
                </div>
                <div className="card-body p-4">
                  <p className="text-muted mb-4">
                    Leave blank to keep the current password. Only fill this if you want to change the password.
                  </p>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <label className="form-label">New Password</label>
                      <input
                        type="password"
                        className={`form-control ${errors.password ? "is-invalid" : ""}`}
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        placeholder="Enter new password (min 6 characters)"
                      />
                      {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Confirm New Password</label>
                      <input
                        type="password"
                        className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                        name="confirmPassword"
                        value={form.confirmPassword}
                        onChange={handleChange}
                        placeholder="Confirm new password"
                      />
                      {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Permissions Section */}
              {form.role === "admin" && (
                <div className="card mb-4">
                  <div className="card-header bg-white py-3">
                    <h5 className="mb-0 d-flex align-items-center">
                      <FaUserShield className="me-2 text-primary" />
                      Permissions
                      <span className="badge bg-primary ms-2">{form.permissions.length} selected</span>
                    </h5>
                  </div>
                  <div className="card-body p-4">
                    <p className="text-muted mb-4">
                      Select the permissions this admin should have. Changes take effect immediately after saving.
                    </p>

                    {Object.entries(PERMISSION_GROUPS).map(([key, group]) => (
                      <div key={key} className="permission-group">
                        <div
                          className="group-header"
                          onClick={() => toggleGroupExpand(key)}
                        >
                          <h5>
                            <span className="me-2">{group.icon}</span>
                            {group.label}
                            <span className="badge bg-secondary ms-2">
                              {group.permissions.filter((p) => form.permissions.includes(p)).length}/{group.permissions.length}
                            </span>
                          </h5>
                          <div className="d-flex align-items-center gap-3">
                            <span
                              className="toggle-all"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleGroupAll(key);
                              }}
                            >
                              {group.permissions.every((p) => form.permissions.includes(p))
                                ? "Deselect All"
                                : "Select All"}
                            </span>
                            {expandedGroups.includes(key) ? <FaChevronUp /> : <FaChevronDown />}
                          </div>
                        </div>
                        {expandedGroups.includes(key) && (
                          <div className="group-body">
                            {group.permissions.map((permission) => (
                              <div
                                key={permission}
                                className={`permission-item ${form.permissions.includes(permission) ? "selected" : ""}`}
                                onClick={() => togglePermission(permission)}
                              >
                                <input
                                  type="checkbox"
                                  checked={form.permissions.includes(permission)}
                                  onChange={() => {}}
                                />
                                <label>{formatPermissionLabel(permission)}</label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {form.role === "superadmin" && (
                <div className="alert alert-warning d-flex align-items-center mb-4">
                  <FaUserShield className="me-3" size={24} />
                  <div>
                    <strong>Super Admin Role</strong>
                    <p className="mb-0">
                      Super Admins automatically have access to all permissions and can manage other admin users.
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="d-flex gap-3 justify-content-end">
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={() => navigate("/admin-management")}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </CommonLayout>
  );
}
